// src/components/SubmitTime/fields/ComboBoxField.tsx
// Modern React implementation of ComboBoxField (single select) custom field
// Uses react-select v5 with custom styling to match legacy appearance

import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import type { SingleValue, StylesConfig } from 'react-select';
import { isSubmitTimeDark, ST_DARK } from '../components/selectDarkMode';
import type { CustomFieldProps, SelectOption } from '../types';
import './ComboBoxField.css';

interface ComboBoxFieldProps extends CustomFieldProps {
  autoFocus?: boolean;
}

export const ComboBoxField: React.FC<ComboBoxFieldProps> = ({
  customField,
  canEdit,
  handleDynamicInputChange,
  UpdateCustomFields,
  autoFocus,
  showRequiredError,
}) =>
{
  const [selectedValue, setSelectedValue] = useState<SelectOption | null>(() =>
  {
    if (customField.Value !== null && customField.Value !== undefined && customField.Value !== '')
    {
      return {
        value: customField.Value,
        label: customField.SelectedValuesText || '',
      };
    }
    return null;
  });

  const [options, setOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load options: from ServiceUrl (API) or inline Options
  useEffect(() =>
  {
    const serviceUrl = (customField as any).ServiceUrl as string | undefined;
    if (serviceUrl)
    {
      const url = serviceUrl.replace(/^api\//, '');
      setIsLoading(true);
      import('@/services/api')
        .then(({ apiService }) => apiService.get<any[]>(url))
        .then(data =>
        {
          const textField = (customField as any).TextField || 'Name';
          const valueField = (customField as any).ValueField || 'Id';
          setOptions(data.map(item => ({ value: item[valueField], label: item[textField] })));
        })
        .catch(err => console.error('Error loading ComboBoxField options:', err))
        .finally(() => setIsLoading(false));
    }
    else if (customField.Options && Array.isArray(customField.Options))
    {
      setOptions(customField.Options.map(opt => ({ value: opt.Value, label: opt.Text })));
    }
  }, [(customField as any).ServiceUrl, customField.Options]);

  // Sync selected value only when switching to a different field or a different saved record,
  // not on every parent re-render — otherwise the user's in-progress selection gets overwritten
  useEffect(() =>
  {
    if (customField.Value !== null && customField.Value !== undefined && customField.Value !== '')
    {
      setSelectedValue({
        value: customField.Value,
        label: customField.SelectedValuesText || '',
      });
    }
    else
    {
      setSelectedValue(null);
    }
  }, [customField.CustomFieldId, customField.CustomFieldValueId]);

  const handleChange = (newValue: SingleValue<SelectOption>) =>
  {
    if (newValue)
    {
      setSelectedValue(newValue);
      handleDynamicInputChange(customField.CustomFieldId, newValue.value, newValue.label);
    }
    else
    {
      setSelectedValue(null);
      handleDynamicInputChange(customField.CustomFieldId, '', '');
    }
  };

  const handleBlur = () =>
  {
    if (UpdateCustomFields)
    {
      UpdateCustomFields(customField);
    }
  };

  const isFieldDisabled = !canEdit || !customField.ConversationIsEnable;

  // Styles matching SelectorsSection (Layer/Project/Type fields)
  const customStyles: StylesConfig<SelectOption, false> = {
    control: (base, state) => ({
      ...base,
      minHeight: '32px',
      height: '32px',
      borderRadius: '2px',
      boxShadow: 'none',
      fontSize: '12px',
      fontFamily: 'Roboto, Arial, sans-serif',
      ...(isSubmitTimeDark() ? { backgroundColor: ST_DARK.controlBg } : {}),
      borderColor: showRequiredError
        ? '#F26C4F'
        : state.isFocused
          ? (isSubmitTimeDark() ? ST_DARK.borderFocus : '#66afe9')
          : (isSubmitTimeDark() ? ST_DARK.border : '#ccc'),
      '&:hover': { borderColor: showRequiredError ? '#F26C4F' : (isSubmitTimeDark() ? ST_DARK.borderFocus : '#66afe9') },
    }),
    valueContainer: base => ({
      ...base,
      height: '32px',
      padding: '0 8px',
    }),
    input: base => ({
      ...base,
      margin: 0,
      padding: 0,
      lineHeight: 'normal',
      ...(isSubmitTimeDark() ? { color: ST_DARK.text } : {}),
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: base => ({ ...base, padding: '4px 6px' }),
    clearIndicator: base => ({ ...base, padding: '4px' }),
    placeholder: base => ({
      ...base,
      fontSize: '12px',
      lineHeight: 'normal',
      color: isSubmitTimeDark() ? ST_DARK.placeholder : '#aaa',
    }),
    singleValue: base => ({
      ...base,
      fontSize: '12px',
      fontFamily: 'Roboto, Arial, sans-serif',
      lineHeight: 'normal',
      color: isSubmitTimeDark() ? ST_DARK.text : 'rgb(158, 149, 135)',
    }),
    menuPortal: base => ({
      ...base,
      zIndex: 1000200,
    }),
    menu: base => ({
      ...base,
      marginTop: 0,
      borderRadius: '2px',
      fontSize: '12px',
      fontFamily: 'Roboto, Arial, sans-serif',
      ...(isSubmitTimeDark()
        ? { backgroundColor: ST_DARK.menuBg, border: `1px solid ${ST_DARK.border}` }
        : {}),
    }),
    menuList: base => ({
      ...base,
      padding: 0,
      maxHeight: '200px',
    }),
    option: (_base, state) => ({
      display: 'block',
      boxSizing: 'border-box' as const,
      width: '100%',
      fontSize: '12px',
      fontFamily: 'Roboto, Arial, sans-serif',
      lineHeight: '1.4',
      padding: '6px 10px',
      backgroundColor: state.isFocused
        ? ST_DARK.optionFocusBg
        : (isSubmitTimeDark() ? ST_DARK.menuBg : '#fff'),
      color: state.isFocused
        ? ST_DARK.optionFocusText
        : (isSubmitTimeDark() ? ST_DARK.optionText : '#333'),
      cursor: 'pointer',
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
  };

  return (
    <div className="corgee_com_custom-fields-combobox">
      <Select<SelectOption>
        value={selectedValue}
        onChange={handleChange}
        onBlur={handleBlur}
        options={options}
        isDisabled={isFieldDisabled}
        isClearable={!isFieldDisabled}
        isSearchable={true}
        isLoading={isLoading}
        placeholder={customField.Placeholder || 'Select...'}
        styles={customStyles}
        menuPortalTarget={document.body}
        menuPlacement="auto"
        autoFocus={autoFocus}
        openMenuOnFocus={autoFocus}
      />
    </div>
  );
};

export default ComboBoxField;
